import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/ToastOverrides.css';
import axios from 'axios';
import { getApiUrl } from './config/api';
import { applyStoredLanguage } from './utils/languagePreference';
import RootPage from './pages/RootPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import SearchResultsPage from './pages/SearchResultsPage';
import PropertyDetailsPage from './pages/PropertyDetailsPage';
import PropertiesPage from './pages/PropertiesPage';
import HostPage from './pages/HostPage';
import HostPropertyDetailsPage from './pages/HostPropertyDetailsPage';
import AddStayPropertyPage from './pages/AddStayPropertyPage';
import HostMessagesPage from './pages/HostMessagesPage';
import WishlistPage from './pages/WishlistPage';
import RoleBasedRoute from './components/RoleBasedRoute';
// Meeting pages
import MeetingRooms from './pages/meetings/MeetingRooms';
import BookRoomFlow from './pages/meetings/BookRoomFlow';
import MyBookings from './pages/meetings/MyBookings';
import RoomAvailabilitySchedulePage from './pages/meetings/RoomAvailabilitySchedulePage';
import MeetingHome from './pages/meetings/MeetingHome';
import Favourite from './pages/meetings/Favourite';
import Profile from './pages/meetings/Profile';
import CreateTeam from './pages/meetings/CreateTeam';
import StatusPage from './pages/meetings/StatusPage';
import MeetingSearchResults from './pages/meetings/MeetingSearchResults';
import MeetingRoomDetailsPage from './pages/meetings/MeetingRoomDetailsPage';
import AddYourSpace from './pages/meetings/AddYourSpace';
import ModernBookingPage from './pages/meetings/ModernBookingPage';
import BookRoom from './pages/meetings/BookRoom';
import BookMeetingRoomPage from './pages/BookMeetingRoomPage';
import BookingPage from './pages/BookingPage';
import BookingPageMeeting from './pages/meetings/BookingPage';
import PaymentPage from './pages/PaymentPage';
import UserPaymentHistory from './pages/UserPaymentHistory';
import MeetingPaymentPage from './pages/meetings/MeetingPaymentPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import TripsPage from './pages/TripsPage';
import BookingDetailsPage from './pages/BookingDetailsPage';
import NotificationsPage from './pages/NotificationsPage';
import AccountPage from './pages/AccountPage';
import HelpPage from './pages/HelpPage';
import FeedbackPage from './pages/FeedbackPage';
import OffersDealsPage from './pages/OffersDealsPage';
import InstallAppPrompt from './components/InstallAppPrompt';
import MeetingBottomNav from './components/MeetingBottomNav';
import { registerPushNotifications } from './services/pushNotificationService';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProperties from './pages/admin/AdminProperties';
import AdminBookings from './pages/admin/AdminBookings';
import AdminPayments from './pages/admin/AdminPayments';
import AdminReviews from './pages/admin/AdminReviews';
import AdminReports from './pages/admin/AdminReports';
import AdminLogs from './pages/admin/AdminLogs';
import WorkspaceRoute from './components/WorkspaceRoute';
import WorkspacePermissionRoute from './components/WorkspacePermissionRoute';
import WorkspaceLayout from './pages/workspace/WorkspaceLayout';
import WorkspaceDashboard from './pages/workspace/WorkspaceDashboard';
import WorkspaceStaff from './pages/workspace/WorkspaceStaff';
import WorkspaceProperties from './pages/workspace/WorkspaceProperties';
import WorkspaceBookings from './pages/workspace/WorkspaceBookings';
import WorkspaceAnalytics from './pages/workspace/WorkspaceAnalytics';
import WorkspaceActivity from './pages/workspace/WorkspaceActivity';
import WorkspaceNotifications from './pages/workspace/WorkspaceNotifications';
import WorkspaceJoinPage from './pages/workspace/WorkspaceJoinPage';

function AppRuntimeEffects() {
  const location = useLocation();

  useEffect(() => {
    registerPushNotifications().catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const retryRegistration = () => {
      registerPushNotifications().catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        retryRegistration();
      }
    };

    window.addEventListener('focus', retryRegistration);
    window.addEventListener('codex-authenticated', retryRegistration);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', retryRegistration);
      window.removeEventListener('codex-authenticated', retryRegistration);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
}

function App() {
  useEffect(() => {
    applyStoredLanguage();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const validateStoredSession = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        await axios.get(getApiUrl('/api/auth/me'), {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        if (isCancelled) return;
        const status = error?.response?.status;
        const code = error?.response?.data?.code;

        if (status === 401 || status === 403 || code === 'AUTH_USER_NOT_FOUND') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('authRedirectPath');
        }
      }
    };

    validateStoredSession();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <Router>
      <AppRuntimeEffects />
      <InstallAppPrompt />
      <Routes>
        <Route path="/" element={<RootPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/join-workspace" element={<WorkspaceJoinPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route 
          path="/search" 
          element={<SearchResultsPage />} 
        />
        <Route 
          path="/property/:propertyId" 
          element={<PropertyDetailsPage />} 
        />
        <Route 
          path="/properties" 
          element={<PropertiesPage />} 
        />
        <Route 
          path="/booking" 
          element={<BookingPage />} 
        />
        <Route 
          path="/payment" 
          element={<PaymentPage />} 
        />
        <Route
          path="/my-payments"
          element={<UserPaymentHistory />}
        />
        <Route
          path="/meetings/payment/:bookingId"
          element={<MeetingPaymentPage />}
        />
        <Route
          path="/meetings/payment"
          element={<MeetingPaymentPage />}
        />
        <Route 
          path="/booking-confirmation" 
          element={<BookingConfirmationPage />} 
        />
        <Route 
          path="/trips" 
          element={<TripsPage />} 
        />
        <Route 
          path="/booking-details/:bookingId" 
          element={<BookingDetailsPage />} 
        />
        <Route 
          path="/notifications" 
          element={<NotificationsPage />} 
        />
        <Route 
          path="/account" 
          element={<AccountPage />} 
        />
        <Route 
          path="/help" 
          element={<HelpPage />} 
        />
        <Route 
          path="/faq" 
          element={<HelpPage />} 
        />
        <Route 
          path="/feedback" 
          element={<FeedbackPage />} 
        />
        <Route 
          path="/stays" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <SearchResultsPage />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/deals" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <SearchResultsPage />
            </RoleBasedRoute>
          } 
        />
        <Route path="/offers-deals" element={<OffersDealsPage />} />
        <Route 
          path="/experiences" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <SearchResultsPage />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/host" 
          element={<HostPage />} 
        />
        <Route 
          path="/host/add-property" 
          element={<AddStayPropertyPage />} 
        />
        <Route 
          path="/host/property/:propertyId" 
          element={<HostPropertyDetailsPage />} 
        />
        <Route 
          path="/host/messages" 
          element={<HostMessagesPage />} 
        />
        <Route
          path="/workspace"
          element={
            <WorkspaceRoute>
              <WorkspaceLayout />
            </WorkspaceRoute>
          }
        >
          <Route index element={<WorkspaceDashboard />} />
          <Route path="staff" element={<WorkspacePermissionRoute permission="manage_staff"><WorkspaceStaff /></WorkspacePermissionRoute>} />
          <Route path="properties" element={<WorkspacePermissionRoute permission="view_properties"><WorkspaceProperties /></WorkspacePermissionRoute>} />
          <Route path="bookings" element={<WorkspacePermissionRoute permission="manage_bookings"><WorkspaceBookings /></WorkspacePermissionRoute>} />
          <Route path="analytics" element={<WorkspacePermissionRoute permission="view_analytics"><WorkspaceAnalytics /></WorkspacePermissionRoute>} />
          <Route path="activity" element={<WorkspacePermissionRoute permission="view_activity"><WorkspaceActivity /></WorkspacePermissionRoute>} />
          <Route path="notifications" element={<WorkspacePermissionRoute permission="view_notifications"><WorkspaceNotifications /></WorkspacePermissionRoute>} />
        </Route>
        <Route 
          path="/wishlist" 
          element={<WishlistPage />} 
        />
        {/* Meeting Routes */}
        <Route 
          path="/meetings/rooms" 
          element={<MeetingRooms />} 
        />
        <Route 
          path="/meetings/room/:roomId" 
          element={<MeetingRoomDetailsPage />} 
        />
        <Route 
          path="/meetings/book" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <BookRoom />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/book-flow" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <BookRoomFlow />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/modern-booking/:roomId" 
          element={<ModernBookingPage />} 
        />
        <Route 
          path="/meetings/booking/:roomId" 
          element={<BookingPageMeeting />} 
        />
        <Route 
          path="/meetings/my-bookings" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <MyBookings />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/schedule" 
          element={<RoomAvailabilitySchedulePage />} 
        />
        {/* Direct Meeting Booking Route - No role restrictions */}
        <Route 
          path="/meeting-book" 
          element={<RoomAvailabilitySchedulePage />} 
        />
        {/* Meeting Home Route - Landing page for meeting booking */}
        <Route 
          path="/meeting-home" 
          element={<MeetingHome />} 
        />
        <Route 
          path="/meetings/favourite" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <Favourite />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/profile" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <Profile />
            </RoleBasedRoute>
          } 
        />
        {/* Create Team Route */}
        <Route 
          path="/meetings/create-team" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <CreateTeam />
            </RoleBasedRoute>
          } 
        />
        {/* Status Page Route */}
        <Route 
          path="/meetings/status" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <StatusPage />
            </RoleBasedRoute>
          } 
        />
        {/* Meeting Search Route - redirects to search-results */}
        <Route 
          path="/meetings/search" 
          element={<MeetingSearchResults />} 
        />
        {/* Meeting Search Results Route */}
        <Route 
          path="/meetings/search-results" 
          element={<MeetingSearchResults />} 
        />
        {/* Add Your Space Route */}
        <Route 
          path="/meetings/add-your-space" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <AddYourSpace />
            </RoleBasedRoute>
          } 
        />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="properties" element={<AdminProperties />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="logs" element={<AdminLogs />} />
        </Route>
      </Routes>
      <MeetingBottomNav />
      {/* Stable wrapper so React and react-toastify don't conflict on unmount (avoids removeChild on Node) */}
      <div key="toast-root">
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

export default App;
